import {useTranslation} from '@/translations/i18n.tsx'
import {ChangeEvent, FC, useRef} from 'react'
import {toast} from '@utilities/toast'
import {Avatar} from '@components/ui/avatar'
import {User} from '@features/user/types.ts'
import {Spinner} from '@components/ui/spinner'
import {useChangeFreelancerProfilePhoto} from '@features/settings/queries/useChangeFreelancerProfilePhoto.ts'
import {errorHandler, raise} from '@utilities/helpers.ts'
import {useAsync} from '@hooks/useAsync.ts'
import {httpUploadFreelancerProfilePhoto} from '@features/settings/http/settings.http.ts'
import {parseAxiosPromise} from '@/axiosInstance.ts'
import {z} from 'zod'
import {captureException} from '@sentry/react'
import {Edit04Icon, ImageUserPlusIcon, Trash01Icon} from '@components/ui/icon'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    DropdownMenuItem
} from '@components/ui/dropdown-menu'

const UPLOAD_LIMIT_MB = 2
const UPLOAD_LIMIT_BYTES = UPLOAD_LIMIT_MB * 1_000_000

const USER_ICON_BACKGROUND_IMAGE =
    "url(\"data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='100' ry='100' stroke='%23D0D5DD' stroke-width='1' stroke-dasharray='5%2c 5' stroke-dashoffset='25' stroke-linecap='butt'/%3e%3c/svg%3e\")"

export const ProfilePicturePicker: FC<{user: User}> = ({user}) => {
    const {t} = useTranslation()
    const invisibleInputRef = useRef<HTMLInputElement>(null)
    const changeFreelancerProfilePhotoMutation = useChangeFreelancerProfilePhoto({userId: user.id})
    const updateFreelancerProfilePhotoMutation = useAsync()
    const openFileSystem = () => invisibleInputRef.current?.click()

    const uploadImage = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] ?? raise('file is nullish')
        if (file.size > UPLOAD_LIMIT_BYTES) {
            toast.error(t('settings:personal_information:upload_limit_exceed', {count: UPLOAD_LIMIT_MB}))
        } else {
            const formData = new FormData()
            formData.append('image', file)
            formData.append('type', 'profile-photo')

            try {
                const response = await updateFreelancerProfilePhotoMutation.run(
                    parseAxiosPromise({
                        axiosPromise: httpUploadFreelancerProfilePhoto({
                            payload: formData,
                            urlParams: {userId: user.id}
                        }),
                        responseShape: z.object({url: z.string().min(1)}),
                        onZodError: captureException
                    })
                )
                changeFreelancerProfilePhotoMutation.mutate({profile_photo_url: response.url})
            } catch (error) {
                errorHandler(error)
            }
        }
    }

    const removeImage = () => {
        changeFreelancerProfilePhotoMutation.mutate({profile_photo_url: null})
    }

    return (
        <div className="flex gap-5">
            <div className="relative">
                {(changeFreelancerProfilePhotoMutation.isPending || updateFreelancerProfilePhotoMutation.isLoading) && (
                    <div className="absolute top-1/2 left-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/50 text-neutral-400">
                        <Spinner size={32} />
                    </div>
                )}

                <div className="cursor-pointer [&_img]:h-full [&_img]:w-full [&_img]:object-cover">
                    {user.profilePhotoUrl ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger
                                className="cursor-pointer"
                                disabled={
                                    changeFreelancerProfilePhotoMutation.isPending ||
                                    updateFreelancerProfilePhotoMutation.isLoading
                                }
                            >
                                <Avatar name={user.firstName} imageUrl={user.profilePhotoUrl ?? ''} size="lg" />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent side="right" align="start" sideOffset={8}>
                                <DropdownMenuItem
                                    onClick={openFileSystem}
                                    disabled={
                                        changeFreelancerProfilePhotoMutation.isPending ||
                                        updateFreelancerProfilePhotoMutation.isLoading
                                    }
                                >
                                    <Edit04Icon />
                                    {t('commons:edit')}
                                </DropdownMenuItem>

                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    variant="destructive"
                                    onClick={event => {
                                        event.preventDefault()
                                        removeImage()
                                    }}
                                    disabled={
                                        changeFreelancerProfilePhotoMutation.isPending ||
                                        updateFreelancerProfilePhotoMutation.isLoading
                                    }
                                >
                                    <Trash01Icon />
                                    {t('commons:remove')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <div
                            className="flex h-14 w-14 items-center justify-center rounded-full bg-white text-neutral-500"
                            style={{backgroundImage: USER_ICON_BACKGROUND_IMAGE}}
                            onClick={openFileSystem}
                        >
                            <ImageUserPlusIcon />
                        </div>
                    )}
                </div>
            </div>

            <input
                className="invisible absolute h-0 w-0"
                disabled={
                    changeFreelancerProfilePhotoMutation.isPending || updateFreelancerProfilePhotoMutation.isLoading
                }
                type="file"
                ref={invisibleInputRef}
                accept="image/*"
                value=""
                onChange={uploadImage}
                multiple={false}
            />
        </div>
    )
}
